<template>
  <base-loader v-if="requisiteStore.loading" />

  <div class="requisite-block" v-else>
    <ul
      class="requisite-list"
      v-if="requisiteStore.requisiteTypes.length > 0"
    >
      <li
        class="card requisite-item"
        v-for="(
          requisiteType, index
        ) in requisiteStore.requisiteTypes"
        :key="index"
      >
        <!-- Заголовок -->
        <div class="item-header">
          <div class="header-top">
            <h3 class="header-title">{{ requisiteType.name }}</h3>
            <base-button
              v-if="!editingRequisiteId"
              class="header-btn"
              @click="startEditing(requisiteType)"
            >
              Редактировать
            </base-button>
          </div>
          <span class="header-desc">
            {{ requisiteType.description }}
          </span>
        </div>

        <div class="item-center">
          <!-- Режим просмотра -->
          <div
            class="center-main"
            v-if="editingRequisiteId !== requisiteType.id"
          >
            <ul
              class="field-list"
              v-if="requisiteStore.hasRequisiteData(requisiteType)"
            >
              <li
                class="field-item"
                v-for="field in requisiteType.schema.fields"
                :key="field.code"
              >
                <base-label
                  class="form-label"
                  :for="`${requisiteType.id}-${field.code}`"
                >
                  {{ field.label }}:
                </base-label>
                <pre
                  :id="`${requisiteType.id}-${field.code}`"
                  class="field-value"
                  >{{
                    requisiteStore.getFieldValue(
                      requisiteType,
                      field.code
                    )
                  }}</pre
                >
              </li>
            </ul>
            <div v-else class="no-data">Информация не заполнена</div>

            <!-- Просмотр файлов -->
            <AttachmentViewer
              v-if="
                requisiteType.allow_attachments &&
                requisiteStore.getAttachments(requisiteType.id)
                  .length > 0
              "
              :attachments="
                requisiteStore.getAttachments(requisiteType.id)
              "
            />

            <div
              class="item-bottom"
              v-if="
                requisiteStore.getRequisiteCreateTime(
                  requisiteType
                ) ||
                requisiteStore.getRequisiteUpdateTime(requisiteType)
              "
            >
              <MetaTime
                :createdAt="
                  requisiteStore.getRequisiteCreateTime(requisiteType)
                "
                :updatedAt="
                  requisiteStore.getRequisiteUpdateTime(requisiteType)
                "
              />
            </div>
          </div>

          <!-- Режим редактирования -->
          <div class="center-editing" v-else>
            <form
              :id="requisiteFormId(requisiteType)"
              class="form"
              @submit.prevent="saveRequisite(requisiteType)"
            >
              <div
                class="form-group"
                v-for="field in requisiteType.schema.fields"
                :key="field.code"
              >
                <base-label
                  class="form-label"
                  :for="`${requisiteType.id}-${field.code}`"
                >
                  {{ field.label }}:
                </base-label>

                <!-- Используем textarea, если тип поля — text-->
                <base-textarea
                  class="form-textarea"
                  v-if="field.type === 'text'"
                  :id="`${requisiteType.id}-${field.code}`"
                  v-model="editFormData[field.code]"
                ></base-textarea>

                <!-- Выпадающий список -->
                <select
                  class="form-select"
                  v-else-if="field.type === 'select'"
                  :id="`${requisiteType.id}-${field.code}`"
                  v-model="editFormData[field.code]"
                >
                  <option disabled value>Выберите значение</option>
                  <option
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>

                <!-- Числа -->
                <base-input
                  class="form-input"
                  v-else-if="field.type === 'number'"
                  :id="`${requisiteType.id}-${field.code}`"
                  v-model="editFormData[field.code]"
                  :type="field.type"
                />

                <!-- Дата и текст -->
                <base-input
                  class="form-input"
                  v-else
                  :id="`${requisiteType.id}-${field.code}`"
                  v-model="editFormData[field.code]"
                  :type="field.type === 'date' ? 'date' : 'text'"
                />
              </div>

              <!-- Прикрепления -->
              <AttachmentUploader
                v-if="requisiteType.allow_attachments"
                :attachment-ids="requisiteStore.attachmentIds[requisiteType.id]"
                :requisite-type-id="requisiteType.id"
                :requisite-code="requisiteType.code"
                :form-id="requisiteFormId(requisiteType)"
                :existing-attachments="
                  requisiteStore.getAttachments(requisiteType.id)
                "
                @update:attachment-ids="
                  requisiteStore.setAttachmentIds(requisiteType.id, $event)
                "
              />

              <div class="form-actions">
                <base-primary-button
                  class="form-btn"
                  type="submit"
                  :form="requisiteFormId(requisiteType)"
                >
                  Сохранить
                </base-primary-button>
                <base-button
                  class="form-btn"
                  type="button"
                  @click="cancelEdit"
                >
                  Отмена
                </base-button>
              </div>
            </form>
          </div>
        </div>
      </li>
    </ul>

    <div class="no-data" v-else>Пользовательские поля отсутствуют</div>
  </div>
</template>

<script>
import AttachmentUploader from "@/components/Attachment/Uploader.vue";
import AttachmentViewer from "@/components/Attachment/Viewer.vue";
import MetaTime from "@/components/Meta/MetaTime.vue";

import { useRequisiteStore } from "@/stores/requisiteStore";
import { useNotificationStore } from "@/stores/notificationStore";

export default {
  name: "RequisiteList",
  components: { AttachmentUploader, AttachmentViewer, MetaTime },

  props: {
    id: { type: [String, Number], required: true },
    allowed_models: { type: Array, required: true },
    requisitable_type: { type: String, required: true },
  },

  data: () => ({
    editingRequisiteId: null,
    editFormData: {},
  }),

  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    requisiteStore() {
      return useRequisiteStore();
    },
  },

  methods: {
    async loadRequisites() {
      try {
        await this.requisiteStore.loadRequisites({
          id: this.id,
          allowed_models: this.allowed_models,
          requisitable_type: this.requisitable_type,
        });
      } catch (error) {
        this.notificationStore.showError(
          "Ошибка при загрузке пользовательских полей"
        );
      }
    },

    startEditing(requisiteType) {
      this.editingRequisiteId = requisiteType.id;
      const req = this.requisiteStore.requisites.find(
        (r) => r.requisite_type.id === requisiteType.id
      );
      this.editFormData = req ? { ...req.data } : {};
    },

    cancelEdit() {
      this.editingRequisiteId = null;
      this.editFormData = {};
    },

    requisiteFormId(requisiteType) {
      return `requisite-edit-form-${requisiteType.id}`;
    },

    async saveRequisite(requisiteType) {
      try {
        await this.requisiteStore.saveRequisite(
          requisiteType,
          this.editFormData,
          this.id,
          this.requisitable_type
        );
        this.cancelEdit();
        await this.loadRequisites();
        this.notificationStore.showSuccess(
          "Данные успешно обновлены"
        );
      } catch (error) {
        console.error("Ошибка при сохранении пользовательских полей", error);
      }
    },
  },

  async created() {
    await this.loadRequisites();
  },
};
</script>

<style scoped>
.requisite-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.item-header {
  display: flex;
  flex-direction: column;
}

.header-top {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 7px;
}

.header-title,
.header-desc {
  max-width: 60%;
}

.header-btn {
  position: absolute;
  right: 0;
  bottom: -14px;
}

.header-desc {
  color: #666;
}

.center-main,
.center-editing {
  margin-top: 20px;
}

.field-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-in-indent);
}

.field-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.field-label {
  margin-right: 5px;
  flex-shrink: 0;
  margin-top: 2px;
}

.field-value {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

.item-bottom {
  margin-top: 20px;
}

.form-actions {
  margin-top: 20px;
}
</style>
